<template>
  <div class="direct-read-post">
    <el-card class="post-card" shadow="hover" v-if="post.answers && post.answers.length > 0">
      <div class="post-content">
        <!-- Display current answer from answers array -->
        <template v-if="currentAnswer">
          <!-- Ending Card Content -->
          <template v-if="currentAnswer.content_type === 'ending'">
            <div class="text-content" v-html="currentAnswer.content"></div>
          </template>

          <!-- Text Content -->
          <template v-else-if="!currentAnswer.content_type || currentAnswer.content_type === 'text'">
            <div class="text-content" v-html="renderContent(currentAnswer.content)"></div>
          </template>

          <!-- Image Content -->
          <template v-else-if="currentAnswer.content_type === 'image'">
            <div class="image-content">
              <img v-if="currentAnswer.fileList && currentAnswer.fileList.length > 0"
                :src="currentAnswer.fileList[0].url"
                :alt="'Post Image'"
              >
            </div>
          </template>

          <!-- Video Content -->
          <template v-else-if="currentAnswer.content_type === 'video'">
            <div class="video-content">
              <video v-if="currentAnswer.fileList && currentAnswer.fileList.length > 0" controls>
                <source :src="currentAnswer.fileList[0].url" :type="currentAnswer.fileList[0].mime_type">
                Your browser does not support the video tag.
              </video>
            </div>
          </template>

          <!-- Quiz Content -->
          <template v-else-if="currentAnswer.content_type === 'quiz'">
            <div class="quiz-content">
              <div class="quiz-question"><strong>Question:</strong> <span v-html="currentAnswer.quiz_content.question"></span></div>
              <div
                v-for="(option, idx) in currentAnswer.quiz_content.options.filter(option => option !== '')"
                :key="idx"
                class="quiz-option"
                :class="{
                  'correct': quiz_answers?.[currentAnswer.uniqueCode] != undefined && ['A','B','C','D'][idx] === currentAnswer.quiz_content.correct_answer,
                  'incorrect': quiz_answers?.[currentAnswer.uniqueCode] != undefined &&
                               quiz_answers?.[currentAnswer.uniqueCode]?.['answer'] !== currentAnswer.quiz_content.correct_answer &&
                               ['A','B','C','D'][idx] === quiz_answers?.[currentAnswer.uniqueCode]?.['answer']
                }"
                @click="selectAndSubmitAnswer(idx)"
              >
                <div class="quiz-option-content">
                  <div style="display: flex; flex: 1; align-items: flex-start;">
                    <span style="min-width: 30px; line-height: 1.5;">{{ ['A','B','C','D'][idx] }}.</span>
                    <span v-html="option" style="line-height: 1.5;"></span>
                  </div>
                  <span v-if="['A','B','C','D'][idx] === currentAnswer.quiz_content.correct_answer && quiz_answers?.[currentAnswer.uniqueCode]" class="el-icon-check correct-marker"></span>
                  <span v-if="['A','B','C','D'][idx] === quiz_answers?.[currentAnswer.uniqueCode]?.['answer'] &&
                              quiz_answers?.[currentAnswer.uniqueCode]?.['answer'] !== currentAnswer.quiz_content.correct_answer"
                        class="el-icon-close incorrect-marker"></span>
                </div>
              </div>
              <div v-if="quiz_answers?.[currentAnswer.uniqueCode]" class="quiz-explanation">
                <div class="explanation-header">Explanation:</div>
                <div class="explanation-content" v-html="currentAnswer.quiz_content.explanation"></div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </el-card>

    <!-- Answer Navigation -->
    <div class="answer-controls" v-if="post.answers && post.answers.length >= 1">
      <el-button
        v-if="currentAnswerIndex > 0"
        class="nav-button prev"
        icon="el-icon-arrow-left"
        circle
        @click="prevAnswer"
      ></el-button>
      <div v-else class="nav-button-placeholder"></div>

      <div class="answer-navigation">
        <span class="page-indicator">#{{ currentAnswerIndex + 1 }} of {{ post.answers.length }}</span>
        <div class="nav-dots">
          <span
            v-for="(answer, index) in post.answers"
            :key="index"
            :class="['nav-dot', { active: index === currentAnswerIndex }, { 'ending-dot': answer.is_ending_card }]"
            @click="goToAnswer(index)"
          ></span>
        </div>
      </div>

      <el-button
        v-if="currentAnswerIndex < post.answers.length - 1"
        class="nav-button next"
        icon="el-icon-arrow-right"
        circle
        @click="nextAnswer"
      ></el-button>
      <div v-else class="nav-button-placeholder"></div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { postRequest } from '@/apis/front'
import { getUserId } from '@/utils/store'

export default {
  name: 'DirectReadPost',
  props: {
    post: {
      type: Object,
      required: true
    },
    showAnswer: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentAnswerIndex: 0,
      quiz_answers: {}
    }
  },
  computed: {
    currentAnswer() {
      if (this.post.answers && this.post.answers.length > 0) {
        return this.post.answers[this.currentAnswerIndex]
      }
      return null
    }
  },
  watch: {
    post: {
      async handler(newPost) {
        // Reset to first answer when post changes
        this.currentAnswerIndex = 0
        this.loadQuizAnswers()

        // Inject ending card into answers array if it has answers
        if (newPost && newPost.answers && newPost.answers.length > 0) {
          // Check if ending card already exists
          const hasEndingCard = newPost.answers.some(answer => answer.is_ending_card)
          if (!hasEndingCard) {
            await this.addEndingCard(newPost)
          }
        }

        this.trackAnswerView()
      },
      immediate: true
    },
    currentAnswerIndex() {
      // Track view when answer index changes
      this.trackAnswerView()

      // Set up ending card button if we're on the ending card
      this.$nextTick(() => {
        if (this.currentAnswer && this.currentAnswer.is_ending_card) {
          this.setupEndingCardButton()
        }
      })
    }
  },
  mounted() {
    this.loadQuizAnswers()
    this.trackAnswerView()
  },
  methods: {
    renderContent(content) {
      if (!content) return ''
      const rawHtml = marked(content)
      return DOMPurify.sanitize(rawHtml)
    },
    prevAnswer() {
      if (this.currentAnswerIndex > 0) {
        this.currentAnswerIndex--
      }
    },
    nextAnswer() {
      if (this.currentAnswerIndex < this.post.answers.length - 1) {
        this.currentAnswerIndex++
        this.$nextTick(() => {
          if (this.currentAnswer && this.currentAnswer.is_ending_card) {
            this.setupEndingCardButton()
          }
        })
      }
    },
    goToAnswer(index) {
      this.currentAnswerIndex = index
      this.$nextTick(() => {
        if (this.currentAnswer && this.currentAnswer.is_ending_card) {
          this.setupEndingCardButton()
        }
      })
    },
    async addEndingCard(post) {
      // Check if takeaway card already exists
      let existingSummary = null
      if (post.view_info && post.view_info.takeaway_card_id) {
        // Fetch the existing takeaway card content
        try {
          const cardResponse = await postRequest('getCard', {
            card_id: post.view_info.takeaway_card_id,
            user_id: getUserId()
          })
          if (cardResponse.data.code === 0 && cardResponse.data.data) {
            existingSummary = cardResponse.data.data.content
          }
        } catch (error) {
          console.error('Error fetching existing takeaway:', error)
        }
      }

      const endingCard = {
        content_type: 'ending',
        content: existingSummary ? this.generateSummaryView(existingSummary) : this.generateButtonView(),
        is_ending_card: true
      }
      post.answers.push(endingCard)
    },

    generateSummaryView(summary) {
      return `
        <div style="text-align: center; padding: 40px 20px;">
          <div style="font-size: 60px; margin-bottom: 20px;">🎉</div>
          <h2 style="font-size: 32px; font-weight: 700; margin-bottom: 16px; color: #303133;">Congratulations!</h2>
          <p style="font-size: 18px; line-height: 1.6; margin-bottom: 40px; color: #606266;">You've completed this stack. Here's your takeaway:</p>
          <div style="background: rgba(103, 194, 58, 0.05); border: 1px solid rgba(103, 194, 58, 0.2); border-radius: 8px; padding: 24px; margin-bottom: 24px; text-align: left;">
            <div style="display: flex; align-items: center; margin-bottom: 12px; color: #67c23a;">
              <span style="font-size: 20px; margin-right: 8px;">📝</span>
              <span style="font-weight: 600; font-size: 16px;">Key Takeaways</span>
            </div>
            <div style="color: #606266; font-size: 16px; line-height: 1.8; white-space: pre-wrap;">${summary}</div>
          </div>
        </div>
      `
    },

    generateButtonView() {
      return `
        <div style="text-align: center; padding: 40px 20px;">
          <div style="font-size: 60px; margin-bottom: 20px;">🎉</div>
          <h2 style="font-size: 32px; font-weight: 700; margin-bottom: 16px; color: #303133;">Congratulations!</h2>
          <p style="font-size: 18px; line-height: 1.6; margin-bottom: 40px; color: #606266;">You've completed this stack. Great job on expanding your knowledge!</p>
          <div style="background: rgba(64, 158, 255, 0.1); border-radius: 8px; padding: 24px; margin-bottom: 24px;">
            <p style="font-size: 16px; margin-bottom: 16px; color: #606266;">Want to save the key insights from this stack?</p>
            <button id="generate-takeaway-btn" style="
              background: #409EFF;
              color: white;
              border: none;
              padding: 12px 24px;
              font-size: 16px;
              font-weight: 600;
              border-radius: 4px;
              cursor: pointer;
              transition: all 0.3s ease;
            " onmouseover="this.style.background='#66b1ff'; this.style.transform='translateY(-2px)'" onmouseout="this.style.background='#409EFF'; this.style.transform='translateY(0)'">
              <span style="margin-right: 8px;">📝</span> Generate a Takeaway Knowit
            </button>
          </div>
          <div id="takeaway-message" style="display: none; margin-top: 20px; padding: 12px; border-radius: 4px;"></div>
        </div>
      `
    },

    setupEndingCardButton() {
      const button = document.getElementById('generate-takeaway-btn')
      const messageDiv = document.getElementById('takeaway-message')

      if (button && !button.dataset.listenerAttached) {
        button.dataset.listenerAttached = 'true'
        button.onclick = async () => {
          button.disabled = true
          button.innerHTML = '⏳ Generating...'

          try {
            const postId = this.post.id || this.post._id?.$oid || this.post._id?.$id
            const response = await postRequest('GeneratePrivateCardOnPost', {
              user_id: getUserId(),
              post_id: postId
            })

            console.log('GeneratePrivateCardOnPost response:', response.data);

            if (response.data.code === 0) {
              messageDiv.style.display = 'block'
              messageDiv.style.background = 'rgba(103, 194, 58, 0.1)'
              messageDiv.style.color = '#67c23a'
              messageDiv.innerHTML = '✓ Takeaway Knowit created! Reloading to show summary...'
              this.$message.success('Takeaway Knowit created!')

              // Log the summary for debugging
              if (response.data.data && response.data.data.summary) {
                console.log('Generated summary:', response.data.data.summary);
              }

              // Trigger parent to reload the post
              setTimeout(() => {
                this.$emit('reload-post')
              }, 1500)
            } else {
              messageDiv.style.display = 'block'
              messageDiv.style.background = 'rgba(245, 108, 108, 0.1)'
              messageDiv.style.color = '#f56c6c'
              messageDiv.innerHTML = `✗ ${response.data.msg || 'Failed to create takeaway'}`
              button.disabled = false
              button.innerHTML = '<span style="margin-right: 8px;">📝</span> Generate a Takeaway Knowit'
            }
          } catch (error) {
            console.error('Error generating takeaway:', error)
            messageDiv.style.display = 'block'
            messageDiv.style.background = 'rgba(245, 108, 108, 0.1)'
            messageDiv.style.color = '#f56c6c'
            messageDiv.innerHTML = '✗ An error occurred while generating takeaway'
            button.disabled = false
            button.innerHTML = '<span style="margin-right: 8px;">📝</span> Generate a Takeaway Knowit'
          }
        }
      }
    },
    async selectAndSubmitAnswer(idx) {
      // Only allow selection if quiz hasn't been answered yet
      if (!this.currentAnswer || this.quiz_answers?.[this.currentAnswer.uniqueCode]) {
        return
      }

      const userId = getUserId()
      const postId = this.post.id || this.post._id?.$oid || this.post._id?.$id
      const uniqueCode = this.currentAnswer.uniqueCode
      const answer = ['A','B','C','D'][idx]

      try {
        const result = await this.uploadQuizAnswer(postId, userId, uniqueCode, answer)
        if (result.data.code === 0) {
          // Update quiz answers from response
          if (result.data.data && result.data.data.quiz_answers) {
            this.quiz_answers = { ...this.quiz_answers, ...result.data.data.quiz_answers }
          }
        }
      } catch (error) {
        console.error('Error submitting quiz answer:', error)
        this.$message.error('Failed to submit answer')
      }
    },
    async uploadQuizAnswer(postId, userId, uniqueCode, answer) {
      const response = await postRequest('uploadQuizAnswer', {
        post_id: postId,
        user_id: userId,
        uniqueCode: uniqueCode,
        answer: answer
      })
      return response
    },
    loadQuizAnswers() {
      // Load quiz answers from post's view_info if available
      if (this.post && this.post.view_info && this.post.view_info.quiz_answers) {
        this.quiz_answers = this.post.view_info.quiz_answers
      }
    },
    async trackAnswerView() {
      try {
        if (!this.currentAnswer || !this.post) {
          return
        }

        const userId = getUserId()
        const postId = this.post.id || this.post._id?.$oid || this.post._id?.$id

        if (!userId || !postId) {
          return
        }

        await postRequest('updatePostView', {
          user_id: userId,
          post_id: postId,
          read_scene: 'inbox',
          view_method: 'FREE',
          read_answer_index: this.currentAnswerIndex,
          answer_uniqueCode: this.currentAnswer.uniqueCode,
          title: this.post.title
        })
      } catch (error) {
        console.error('Failed to track view:', error)
      }
    }
  }
}
</script>

<style scoped>
.direct-read-post {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.post-card {
  width: 100%;
  aspect-ratio: 3 / 4;
  display: flex;
  flex-direction: column;
  position: relative;
}

:deep(.post-card.el-card) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.post-card .el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 40px;
  overflow-y: auto;
}

.post-content {
  flex: 1;
  margin-bottom: 200px;
}

/* Text Content Styling */
.text-content {
  font-size: 20px;
  line-height: 1.7;
  color: #333;
  word-wrap: break-word;
}

.text-content :deep(h1),
.text-content :deep(h2),
.text-content :deep(h3),
.text-content :deep(h4),
.text-content :deep(h5),
.text-content :deep(h6) {
  margin-top: 32px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: #1a1a1a;
}

.text-content :deep(h1) { font-size: 32px; }
.text-content :deep(h2) { font-size: 28px; }
.text-content :deep(h3) { font-size: 24px; }
.text-content :deep(h4) { font-size: 22px; }
.text-content :deep(h5) { font-size: 20px; }
.text-content :deep(h6) { font-size: 18px; }

.text-content :deep(p) {
  margin: 0 0 16px 0;
  line-height: 1.7;
}

.text-content :deep(p:last-child) {
  margin-bottom: 0;
}

.text-content :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

.text-content :deep(em) {
  font-style: italic;
}

.text-content :deep(a) {
  color: #409eff;
  text-decoration: none;
  border-bottom: 1px solid #409eff;
}

.text-content :deep(a:hover) {
  color: #66b1ff;
  border-bottom-color: #66b1ff;
}

.text-content :deep(ul),
.text-content :deep(ol) {
  margin: 16px 0;
  padding-left: 28px;
}

.text-content :deep(li) {
  margin: 8px 0;
  line-height: 1.7;
}

.text-content :deep(blockquote) {
  margin: 20px 0;
  padding: 16px 20px;
  background: #f8f9fa;
  border-left: 4px solid #409eff;
  color: #606266;
}

.text-content :deep(code) {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
  color: #e83e8c;
}

.text-content :deep(pre) {
  background: #f5f7fa;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 16px 0;
}

.text-content :deep(pre code) {
  background: transparent;
  padding: 0;
  color: #333;
}

.text-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 16px 0;
}

/* Image Content */
.image-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 400px;
}

.image-content img {
  max-width: 100%;
  max-height: 600px;
  object-fit: contain;
  border-radius: 8px;
}

/* Video Content */
.video-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 400px;
}

.video-content video {
  width: 100%;
  max-width: 100%;
  border-radius: 8px;
}

/* Quiz Content */
.quiz-content {
  pointer-events: auto;
  position: relative;
}

.quiz-question {
  font-size: 20px;
  margin-bottom: 20px;
  line-height: 1.6;
}

.quiz-question strong {
  color: #409eff;
  margin-right: 8px;
}

.quiz-option {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 12px 20px;
  margin-bottom: 12px;
  background-color: #FFFFFF;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.quiz-option:hover {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.quiz-option.selected {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.quiz-option.correct {
  border-color: #67c23a;
  background-color: #f0f9ff;
  cursor: default;
}

.quiz-option.incorrect {
  border-color: #f56c6c;
  background-color: #fef0f0;
  cursor: default;
}

.quiz-option-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  font-size: 18px;
  line-height: 1.5;
}

.correct-marker {
  color: #67c23a;
  font-size: 20px;
  font-weight: bold;
  margin-left: 12px;
  flex-shrink: 0;
}

.incorrect-marker {
  color: #f56c6c;
  font-size: 20px;
  font-weight: bold;
  margin-left: 12px;
  flex-shrink: 0;
}


.quiz-explanation {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid #e4e7ed;
}

.explanation-header {
  font-weight: 600;
  color: #409eff;
  margin-bottom: 12px;
  font-size: 18px;
}

.explanation-content {
  line-height: 1.6;
  color: #606266;
  font-size: 18px;
}

/* Card Footer */
.card-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px;
  border-top: 1px solid #EBEEF5;
  padding: 6px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  z-index: 1;
}

.post-number {
  color: #909399;
  font-size: 14px;
}

.post-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Answer Navigation Controls */
.answer-controls {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
  margin-top: 12px;
}

.nav-button {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

:deep(.nav-button i) {
  margin: 0;
}

.nav-button-placeholder {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.answer-navigation {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.page-indicator {
  font-size: 11px;
  color: #909399;
  font-weight: 500;
}

.nav-dots {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #dcdfe6;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-dot:hover {
  background-color: #909399;
}

.nav-dot.active {
  background-color: #409eff;
  width: 10px;
  height: 10px;
}

.nav-dot.ending-dot {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.nav-dot.ending-dot.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transform: scale(1.2);
}
</style>
